import { FrameWeather } from 'apps/manage/types';
import { Role } from './role';
import { string } from './utils';

export interface Organization {
  id: string;
  name: string;
  logo?: Media;
  roles: Role[];
}

export function isOrganization(data?: any): data is Organization {
  if (!data || typeof data !== 'object') return false;
  if (!string(data.id)) return false;
  if (!string(data.name)) return false;
  return true;
}

export interface UpdateOrganization {
  name?: string;
  logo?: File | Blob;
}

export interface App {
  id: string;
  displayName: string;
  description?: string;
  link: string;
}

export enum AppInstanceState {
  ACTIVATED = 'ACTIVATED',
  INSTALLED = 'INSTALLED',
  DEACTIVATED = 'DEACTIVATED',
  REMOVED = 'REMOVED',
}

export interface AppInstance {
  id: string;
  state: AppInstanceState;
  app: App;
  frame: Frame;
}

export enum FrameState {
  ONLINE = 'ONLINE',
  STANDBY = 'STANDBY',
  OFFLINE = 'OFFLINE',
  IN_VIDEO_CALL = 'IN_VIDEO_CALL',
  SLEEPING = 'SLEEPING',
}

export function isFrameState(data?: any): data is FrameState {
  if (!data || typeof data !== 'string') return false;
  return !!Object.values(FrameState).find(state => state === data);
}

export interface WiFi {
  ssid: string;
  active: boolean;
  ip: string;
}

export type FrameTimeFormat = '15:04' | '3:04PM';
export function isFrameTimeFormat(data?: any): data is FrameTimeFormat {
  if (!data || typeof data !== 'string') return false;
  return data === '15:04' || data === '3:04PM';
}

export interface Frame {
  id: string;
  name?: string;
  serialNumber: string;
  state: FrameState;
  wifis?: WiFi[];
  location?: string;
  macAddress?: string;
  videocallsDisabled?: boolean;
  videocallsCountdownDuration?: number;
  language: string;
  timezone: string;
  softwareVersion?: string;
  keyword?: string;
  productCode?: string;

  hideClock: boolean;
  timeFormat: FrameTimeFormat;
  weather: FrameWeather;
}

export type AspectRatio = [number, number];
export type Resolution = [number, number];

export interface SleepInterval {
  id?: string;
  active: boolean;
  startTime: Date;
  endTime: Date;
  repeat: string[];
}

export function isFrame(data?: any): data is Frame {
  if (!data || typeof data !== 'object') return false;
  if (typeof data.id !== 'string') return false;
  if (typeof data.serialNumber !== 'string') return false;
  if (!isFrameState(data.state)) return false;
  return true;
}

export interface UpdateFrame {
  name?: string;
  location?: string;
  videocallsDisabled?: boolean;
  language?: string;
  timezone?: string;
}

export interface Media {
  id?: string;
  url: string;
}

export enum UserAccessLevel {
  USER = 'USER',
  MANAGER = 'MANAGER',
  ADMIN = 'ADMINISTRATOR',
}

export function isUserAccessLevel(data?: any): data is UserAccessLevel {
  if (!data || typeof data !== 'string') return false;
  return !!Object.values(UserAccessLevel).find(level => level === data);
}

export enum UserStatus {
  DELETED = 'DELETED',
  BLOCKED = 'BLOCKED',
  INVITED = 'INVITED',
  ACTIVE = 'ACTIVE',
}

export function isUserStatus(data?: any): data is UserStatus {
  if (!data || typeof data !== 'string') return false;
  return !!Object.values(FrameState).find(status => status === data);
}

export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  language: string;

  profilePicture?: Media;

  organization?: Organization;
  role: UserAccessLevel;
  state: UserStatus;
  showGetStarted?: boolean;
}

export function isUser(data?: any): data is User {
  if (!data || typeof data !== 'object') return false;
  if (typeof data.id !== 'string') return false;
  if (typeof data.firstName !== 'string') return false;
  if (typeof data.lastName !== 'string') return false;
  if (typeof data.email !== 'string') return false;
  if (!isUserStatus(data.state)) return false;
  if (!isUserAccessLevel(data.role)) return false;
  return true;
}

export interface UpdateUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: UserAccessLevel;
  language?: string;
}

export interface InviteUser {
  email: string;
  firstName: string;
  lastName: string;
  role?: UserAccessLevel;
}

export interface UpdateProfile {
  phone?: string;
  profilePicture?: File | Blob;
  language?: string;
  showGetStarted?: boolean;
}

export interface FrameStory {
  frame: Frame;
  firstShown?: Date;
  lastShown?: Date;
  delivered?: Date;
}

export interface StoryPermissions {
  edit: boolean;
  delete: boolean;
}

export interface Story {
  id: string;
  app: App;
  author: User;
  frames: FrameStory[];

  message?: string;
  media?: Media;

  expirationDate: string;
  expirationTime?: string;
  duration?: number;

  scheduledAt?: string;

  backgroundColor: string;
  textColor: string;

  createdAt: Date;
  updatedAt: Date;
  __typename?: string;

  permissions?: StoryPermissions;
}

export function isStory(data?: any): data is Story {
  if (typeof data !== 'object') return false;
  if (!data.id) return false;
  if (!data.message && !data.media) return false;
  return true;
}

export interface VideoCall {
  id: string;
  initiator?: User;
  frame: Frame;
  state: string;
  duration: number;
  startedAt: Date;
  endedAt: Date;
  __typename?: string;
}

export function isVideoCall(data?: any): data is VideoCall {
  if (typeof data !== 'object') return false;
  if (!data.id) return false;
  if (typeof data.frame !== 'object') return false;
  if (typeof data.duration !== 'number') return false;
  return true;
}

export type ActivityType = Story | VideoCall;
export function isActivityType(data?: any): data is ActivityType {
  return isStory(data) || isVideoCall(data);
}

export interface Team {
  id: string;
  name: string;
  logo?: Media;
  people: User[];
}

export interface UpdateStory {
  expirationDate: Date;
  expirationTime?: Date;
}
